import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShieldAlert, AlertTriangle, Activity, IndianRupee, KeyRound, Search, Download, Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const SEVERITY_STYLES = {
  INFO:     'bg-blue-50    text-blue-700    border-blue-200',
  WARNING:  'bg-yellow-50  text-yellow-800  border-yellow-200',
  HIGH:     'bg-orange-50  text-orange-800  border-orange-200',
  CRITICAL: 'bg-rose-50    text-rose-800    border-rose-200',
};

const MODULES = ['Authentication', 'Employees', 'Roles & Permissions', 'Payroll', 'Leaves',
  'Reimbursements', 'Recruitment', 'Documents', 'Training', 'Company Settings', 'Security'];

const fmtTS = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return s; } };

const Kpi = ({ label, value, icon: Icon, color, testid }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className="font-heading text-3xl font-bold mt-1 text-slate-900">{value}</div>
      </div>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
    </div>
  </div>
);

const SeverityBadge = ({ s }) => (
  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${SEVERITY_STYLES[s] || SEVERITY_STYLES.INFO}`}>
    {s}
  </span>
);

export default function AuditLogs() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);

  // Filters
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({
    start_date: '', end_date: '', module: 'all', severity: 'all', success: 'all', action: '',
  });

  const buildQueryString = () => {
    const params = new URLSearchParams();
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.module !== 'all') params.append('module', filters.module);
    if (filters.severity !== 'all') params.append('severity', filters.severity);
    if (filters.success !== 'all') params.append('success', filters.success);
    if (filters.action) params.append('action', filters.action);
    if (q) params.append('q', q);
    params.append('limit', String(pageSize));
    params.append('skip', String(page * pageSize));
    return params.toString();
  };

  // Single effect: any input change triggers a fresh fetch. Inlined so the lint rule
  // about "setState in effect" doesn't fire on a referenced `load()` helper.
  const filterKey = `${page}|${filters.start_date}|${filters.end_date}|${filters.module}|${filters.severity}|${filters.success}|${filters.action}|${q}`;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, l] = await Promise.all([
          api.get('/audit-logs/summary'),
          api.get(`/audit-logs?${buildQueryString()}`),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setRows(l.data.rows);
        setTotal(l.data.total);
      } catch (e) { if (!cancelled) toast.error('Could not load audit logs'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const downloadCsv = async () => {
    try {
      const url = `/audit-logs.csv?${buildQueryString()}`;
      const { data: blob } = await api.get(url, { responseType: 'blob' });
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 1000);
    } catch (e) { toast.error('CSV export failed'); }
  };

  const clearFilters = () => {
    setPage(0);
    setFilters({ start_date: '', end_date: '', module: 'all', severity: 'all', success: 'all', action: '' });
    setQ('');
  };

  const activeFilterCount = useMemo(() =>
    (filters.module !== 'all' ? 1 : 0) +
    (filters.severity !== 'all' ? 1 : 0) +
    (filters.success !== 'all' ? 1 : 0) +
    (filters.start_date ? 1 : 0) +
    (filters.end_date ? 1 : 0) +
    (filters.action ? 1 : 0) +
    (q ? 1 : 0)
  , [filters, q]);

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Security & Audit</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Audit Logs</h1>
          <p className="text-slate-500 mt-1">Every important action is recorded here. Logs are immutable — they cannot be edited or deleted from the UI.</p>
        </div>
        <Button onClick={downloadCsv} variant="outline" className="rounded-full h-10" data-testid="export-csv">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Kpi label="Today · Total" value={summary.today.total} icon={Activity} color="bg-slate-50 text-slate-600" testid="kpi-total" />
          <Kpi label="Security Events" value={summary.today.security} icon={ShieldAlert} color="bg-orange-50 text-orange-600" testid="kpi-security" />
          <Kpi label="Failed Actions" value={summary.today.failed} icon={AlertTriangle} color="bg-rose-50 text-rose-600" testid="kpi-failed" />
          <Kpi label="Payroll Actions" value={summary.today.payroll} icon={IndianRupee} color="bg-emerald-50 text-emerald-600" testid="kpi-payroll" />
          <Kpi label="Logins" value={summary.today.login} icon={KeyRound} color="bg-blue-50 text-blue-600" testid="kpi-login" />
        </div>
      )}

      {summary?.security_events?.length > 0 && (
        <div className="bg-white border border-orange-200 rounded-2xl p-5" data-testid="security-events-panel">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-5 h-5 text-orange-600" strokeWidth={1.8} />
            <h2 className="font-heading text-lg font-semibold text-slate-900">Recent Security Events</h2>
            <span className="text-[10px] uppercase tracking-wider font-bold bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">{summary.security_events.length}</span>
          </div>
          <div className="space-y-1.5">
            {summary.security_events.slice(0, 5).map(e => (
              <div key={e.id} className="flex items-center justify-between gap-3 text-xs bg-orange-50/40 border border-orange-100 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <SeverityBadge s={e.severity} />
                  <span className="font-mono text-slate-700 truncate">{e.action_type}</span>
                  <span className="text-slate-500 truncate">· {e.user_name || e.user_email || 'unknown'}</span>
                </div>
                <span className="text-slate-400 whitespace-nowrap">{fmtTS(e.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="audit-filters">
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="audit-search" placeholder="Search by user, action, target id…"
                 value={q} onChange={(e) => { setPage(0); setQ(e.target.value); }}
                 onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setQ(e.target.value); } }}
                 className="flex-1 outline-none bg-transparent text-sm" />
          <Button size="sm" onClick={() => setPage(0)} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E] h-8 text-xs">Search</Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 inline-flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5" /> Filters
          </span>
          <Input data-testid="filter-start" type="date" value={filters.start_date} onChange={(e) => { setPage(0); setFilters({ ...filters, start_date: e.target.value }); }} className="h-9 rounded-full text-xs w-auto" />
          <Input data-testid="filter-end" type="date" value={filters.end_date} onChange={(e) => { setPage(0); setFilters({ ...filters, end_date: e.target.value }); }} className="h-9 rounded-full text-xs w-auto" />
          <Select value={filters.module} onValueChange={(v) => { setPage(0); setFilters({ ...filters, module: v }); }}>
            <SelectTrigger data-testid="filter-module" className="h-9 rounded-full text-xs px-3 w-auto min-w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any module</SelectItem>
              {MODULES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.severity} onValueChange={(v) => { setPage(0); setFilters({ ...filters, severity: v }); }}>
            <SelectTrigger data-testid="filter-severity" className="h-9 rounded-full text-xs px-3 w-auto min-w-[120px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any severity</SelectItem>
              <SelectItem value="INFO">INFO</SelectItem>
              <SelectItem value="WARNING">WARNING</SelectItem>
              <SelectItem value="HIGH">HIGH</SelectItem>
              <SelectItem value="CRITICAL">CRITICAL</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.success} onValueChange={(v) => { setPage(0); setFilters({ ...filters, success: v }); }}>
            <SelectTrigger data-testid="filter-success" className="h-9 rounded-full text-xs px-3 w-auto min-w-[110px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any result</SelectItem>
              <SelectItem value="true">Success</SelectItem>
              <SelectItem value="false">Failure</SelectItem>
            </SelectContent>
          </Select>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} data-testid="clear-filters"
                    className="text-xs text-slate-500 hover:text-rose-600 inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-rose-50">
              <X className="w-3 h-3" /> Clear {activeFilterCount}
            </button>
          )}
          <span className="ml-auto text-xs text-slate-500" data-testid="row-count">
            <b className="text-slate-900">{total}</b> log{total !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="audit-table">
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 bg-slate-50 px-4 py-3 border-b border-slate-200">
          <div className="col-span-2">Timestamp</div>
          <div className="col-span-2">User</div>
          <div className="col-span-1">Role</div>
          <div className="col-span-2">Module</div>
          <div className="col-span-3">Action</div>
          <div className="col-span-1">Result</div>
          <div className="col-span-1">Severity</div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>}
          {!loading && rows.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No audit logs match the current filters.</div>}
          {rows.map(r => (
            <div key={r.id} data-testid={`audit-row-${r.id}`}
                 className="grid grid-cols-12 gap-2 text-xs px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50">
              <div className="col-span-2 text-slate-600 font-mono text-[11px]">{fmtTS(r.timestamp)}</div>
              <div className="col-span-2 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{r.user_name || '—'}</div>
                <div className="text-[10px] text-slate-500 truncate">{r.user_email || ''}</div>
              </div>
              <div className="col-span-1 text-slate-600 truncate">{r.role_at_time || '—'}</div>
              <div className="col-span-2 text-slate-700 truncate">{r.module_name || '—'}</div>
              <div className="col-span-3 min-w-0">
                <code className="text-slate-800 text-[11px]">{r.action_type || '—'}</code>
                {r.target_record_name && <div className="text-[10px] text-slate-500 truncate">→ {r.target_record_name}</div>}
              </div>
              <div className="col-span-1">
                {typeof r.success === 'boolean' ? (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.success ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                    {r.success ? 'OK' : 'FAIL'}
                  </span>
                ) : <span className="text-[10px] text-slate-400">—</span>}
              </div>
              <div className="col-span-1">{r.severity ? <SeverityBadge s={r.severity} /> : <span className="text-[10px] text-slate-400">—</span>}</div>
            </div>
          ))}
        </div>
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 text-xs">
            <span className="text-slate-500">Page <b className="text-slate-900">{page + 1}</b> of {Math.ceil(total / pageSize)}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} className="rounded-full h-7" data-testid="prev-page">
                <ChevronLeft className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" disabled={(page + 1) * pageSize >= total} onClick={() => setPage(p => p + 1)} className="rounded-full h-7" data-testid="next-page">
                <ChevronRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
